import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { useNavigate } from '@tanstack/react-router'
import { adminService, Category } from '../services/adminService'

import { CategoryList } from '../components/CategoryList'
import { CategoryTranslationModal } from '../components/CategoryTranslationModal'
import { BulkTranslationModal } from '../components/BulkTranslationModal'
import { TranslationProgressModal } from '../components/TranslationProgressModal'
import { DeepSeekSetupNotice } from '../components/DeepSeekSetupNotice'

import { useTranslation } from '../i18n/hooks/useTranslation'
import { TranslationProgress } from '../utils/constants'
import { translationService } from '../services/translationService'
import { useLanguages, convertToTranslationFormat } from '../hooks/useLanguages'
import { Button } from '../components/ui/Button'

export function CategoriesPage() {
  const { t, currentLanguage } = useTranslation()
  const navigate = useNavigate()
  const [showTranslations, setShowTranslations] = useState<string | null>(null)
  const [showBulkTranslation, setShowBulkTranslation] = useState(false)
  const [showBulkProgressModal, setShowBulkProgressModal] = useState(false)
  const [togglingCategoryId, setTogglingCategoryId] = useState<string | null>(null)

  const [bulkTranslationProgress, setBulkTranslationProgress] = useState<Record<string, TranslationProgress>>({})

  // Helper function to generate slug from text
  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .trim() // Remove leading/trailing spaces
      .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
  }

  const queryClient = useQueryClient()
  
  // Use dynamic languages from database
  const { languages: dynamicLanguages } = useLanguages()
  const SUPPORTED_LANGUAGES = convertToTranslationFormat(dynamicLanguages)

  const { data: categories = [], isLoading, error } = useQuery({
    queryKey: ['categories', currentLanguage],
    queryFn: () => adminService.getCategories(currentLanguage),
  })

  // Categories are now filtered directly by the EnhancedTable component

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminService.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      toast.success(t('categories.notifications.deletedSuccess'))
    },
    onError: () => {
      toast.error(t('categories.notifications.deleteFailed'))
    },
  })

  const toggleStatusMutation = useMutation({
    mutationFn: (id: string) => adminService.updateCategory(id, { 
      status: categories.find(c => c.id === id)?.status === 'active' ? 'inactive' : 'active' 
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      toast.success(t('categories.notifications.statusUpdated'))
      setTogglingCategoryId(null)
    },
    onError: () => {
      toast.error(t('categories.notifications.statusUpdateFailed'))
      setTogglingCategoryId(null)
    },
  })



  const handleEdit = (category: Category) => {
    console.log('Navigating to category edit:', category.id)
    navigate({ to: `/categories/${category.id}` })
  }



  const handleTranslations = (categoryId: string) => {
    setShowTranslations(categoryId)
  }

  const handleToggleStatus = (category: Category) => {
    setTogglingCategoryId(category.id)
    toggleStatusMutation.mutate(category.id)
  }

  const handleDelete = (category: Category) => {
    deleteMutation.mutate(category.id)
  }

  const handleBulkTranslation = async (sourceLanguage: string, targetLanguages: string[], categoryIds: string[], maxConcurrency: number = 5) => {
    setShowBulkTranslation(false)
    
    // Initialize progress tracking
    const progress: Record<string, TranslationProgress> = {}
    categoryIds.forEach(categoryId => {
      targetLanguages.forEach(lang => {
        const key = `${categoryId}-${lang}`
        progress[key] = {
          language: lang,
          status: 'pending'
        }
      })
    })
    setBulkTranslationProgress(progress)
    setShowBulkProgressModal(true)

    // Configure translation service
    translationService.setConfig({
      temperature: 1.3,
      useJsonMode: true
    })

    // Process bulk translations in parallel
    const processTranslations = async () => {
      // Always get English categories for source content, regardless of current language
      const englishCategories = await adminService.getCategories('en')
      
      // Prepare all translation requests
      const allRequests: Array<{
        request: any,
        categoryId: string,
        targetLang: string,
        category: any,
        key: string
      }> = []

      for (const categoryId of categoryIds) {
        // Use English category data to ensure we have the correct source names
        const category = englishCategories.find(c => c.id === categoryId)
        if (!category) continue
        
        // For English source language, always use the English category data directly
        // to avoid issues with corrupted English translations in the database
        let sourceContent
        if (sourceLanguage === 'en' && category.name) {
          console.log(`📝 Using English category data directly for ${category.name}`)
          sourceContent = {
            id: '',
            categoryId: categoryId,
            language: 'en',
            name: category.name,
            description: category.description || '',
            slug: category.slug || generateSlug(category.name)
          }
        } else {
          // For non-English source languages, look for existing translations
          const existingTranslations = await adminService.getCategoryTranslations(categoryId)
          sourceContent = existingTranslations.find(t => t.language === sourceLanguage)
          
          console.log(`🔍 Source content search for ${category.name}:`, {
            categoryId,
            sourceLanguage,
            existingTranslationsCount: existingTranslations.length,
            foundSourceContent: sourceContent ? { id: sourceContent.id, language: sourceContent.language, name: sourceContent.name } : null
          })
        }
        
        if (!sourceContent?.name) {
          // Mark all target languages for this category as error
          targetLanguages.forEach(lang => {
            const key = `${categoryId}-${lang}`
            setBulkTranslationProgress(prev => ({
              ...prev,
              [key]: { 
                ...prev[key], 
                status: 'error', 
                error: `No ${sourceLanguage} translation found for category ${category.name || categoryId}`
              }
            }))
          })
          continue
        }

        for (const targetLang of targetLanguages) {
          const key = `${categoryId}-${targetLang}`
          const translationRequest = {
            sourceLanguage,
            targetLanguage: targetLang,
            text: {
              name: sourceContent.name,
              description: sourceContent.description || ''
            },
            temperature: 1.3,
            useJsonMode: true
          }
          
          console.log(`🔍 Preparing translation request for ${category.name || categoryId}:`, {
            sourceLanguage,
            targetLanguage: targetLang,
            sourceContent: {
              name: sourceContent.name,
              description: sourceContent.description
            },
            key
          })
          
          allRequests.push({
            request: translationRequest,
            categoryId,
            targetLang,
            category,
            key
          })
        }
      }

      // Mark all translations as starting
      allRequests.forEach(({ key }) => {
        setBulkTranslationProgress(prev => ({
          ...prev,
          [key]: { ...prev[key], status: 'translating', startTime: Date.now() }
        }))
      })

      try {
        console.log(`🚀 Starting bulk translation of ${allRequests.length} category translations`);
        
        // Process translations in parallel with progress tracking
        await translationService.translateParallel(
          allRequests.map(item => item.request),
          async (completed, _, result, error, requestIndex) => {
            // Find which translation this result is for using the correct index
            const requestData = allRequests[requestIndex!]
            const { categoryId, targetLang, category, key } = requestData

            console.log(`📥 Received translation result for ${category.name || categoryId} -> ${targetLang}:`, {
              hasError: !!error,
              error: error?.message || error,
              result: result ? {
                name: result.name,
                description: result.description?.substring(0, 50) + '...',
                isLikelyFallback: result.name.includes('[') || result.name.includes('(')
              } : null
            });

            if (error) {
              setBulkTranslationProgress(prev => ({
                ...prev,
                [key]: { 
                  ...prev[key], 
                  status: 'error', 
                  endTime: Date.now(),
                  error: error instanceof Error ? error.message : 'Translation failed'
                }
              }))

              const languageName = SUPPORTED_LANGUAGES.find(l => l.code === targetLang)?.name || targetLang
              toast.error(`Failed to translate ${category.name} to ${languageName}: ${error instanceof Error ? error.message : 'Translation failed'}`)
            } else if (result) {
              // Update progress to completed
              setBulkTranslationProgress(prev => ({
                ...prev,
                [key]: { 
                  ...prev[key], 
                  status: 'completed',
                  endTime: Date.now(),
                  translatedText: result.name
                }
              }))

              // Update progress to saving
              setBulkTranslationProgress(prev => ({
                ...prev,
                [key]: { 
                  ...prev[key], 
                  status: 'saving'
                }
              }))

              // Auto-save translation to database (check for existing first)
              try {
                console.log(`💾 Processing translation save for ${category.name}:`, {
                  categoryId,
                  targetLang,
                  translationResult: result
                })
                
                // First check if translation already exists
                const translations = await adminService.getCategoryTranslations(categoryId)
                const existing = translations.find(t => t.language === targetLang)
                
                console.log(`🔍 Existing translation check:`, {
                  categoryId,
                  targetLang,
                  existingCount: translations.length,
                  existingForLanguage: existing ? existing.id : 'none'
                })
                
                if (existing) {
                  // Update existing translation
                  const updateData = {
                    name: result.name,
                    description: result.description || existing.description || '',
                    slug: result.slug || existing.slug || generateSlug(result.name)
                  }
                  
                  console.log(`📝 Updating existing translation ${existing.id} with:`, updateData)
                  await adminService.updateCategoryTranslation(existing.id, updateData)
                  const languageName = SUPPORTED_LANGUAGES.find(l => l.code === targetLang)?.name || targetLang
                  console.log(`✅ Updated existing translation: ${category.name} -> ${languageName}`, {
                    categoryId,
                    targetLanguage: targetLang,
                    translatedName: result.name,
                    isLikelyFallback: result.name.includes(`[${languageName}]`) || result.name.includes(`(${languageName})`),
                    key: key
                  })
                } else {
                  // Create new translation
                  await adminService.createCategoryTranslation({
                    categoryId,
                    language: targetLang,
                    name: result.name,
                    description: result.description || '',
                    slug: result.slug || generateSlug(result.name),
                    metadata: {
                      createdAt: new Date() as any,
                      updatedAt: new Date() as any,
                      quality: 'draft' as const
                    }
                  })
                  const languageName = SUPPORTED_LANGUAGES.find(l => l.code === targetLang)?.name || targetLang
                  console.log(`✅ Created new translation: ${category.name} -> ${languageName}`, {
                    categoryId,
                    targetLanguage: targetLang,
                    translatedName: result.name,
                    isLikelyFallback: result.name.includes(`[${languageName}]`) || result.name.includes(`(${languageName})`),
                    key: key
                  })
                }

                // Update progress to saved
                setBulkTranslationProgress(prev => ({
                  ...prev,
                  [key]: { 
                    ...prev[key], 
                    status: 'saved'
                  }
                }))

              } catch (dbError) {
                console.error('Failed to save translation to database:', dbError)
                setBulkTranslationProgress(prev => ({
                  ...prev,
                  [key]: { 
                    ...prev[key], 
                    status: 'error',
                    error: 'Failed to save to database'
                  }
                }))
              }

              setBulkTranslationProgress(prev => ({
                ...prev,
                [key]: { 
                  ...prev[key], 
                  status: 'saved', 
                  endTime: Date.now()
                }
              }))

              const languageName = SUPPORTED_LANGUAGES.find(l => l.code === targetLang)?.name || targetLang
              // Check if this is a fallback translation (contains language indicator)
              const isFallback = result.name.includes(`(${languageName})`)
              const translationType = isFallback ? '⚠️ Fallback' : '✅ AI Translated'
              console.log(`${translationType}: ${category.name} → ${languageName} (${result.name})`)
            }
          },
          maxConcurrency // Use configured concurrency for bulk operations
        )

        // All translations completed - show final status
        const finalProgress = Object.values(bulkTranslationProgress)
        const savedCount = finalProgress.filter(p => p.status === 'saved').length
        const errorCount = finalProgress.filter(p => p.status === 'error').length
        const totalRequested = allRequests.length
        
        if (savedCount > 0) {
          toast.success(`Successfully translated and saved ${savedCount}/${totalRequested} translations`)
        }
        if (errorCount > 0) {
          toast.error(`Failed to process ${errorCount}/${totalRequested} translations`)
        }
        
        queryClient.invalidateQueries({ queryKey: ['categories'] })
        
      } catch (error) {
        console.error('Bulk translation failed:', error)
        toast.error('Bulk translation process failed')
      }
    }

    // Start the translation process
    processTranslations()
  }



  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('common.loading')}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <DeepSeekSetupNotice />
      
      {/* Enhanced Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
              {t('categories.title')}
            </h1>
            <p className="text-gray-600 mt-2 text-sm lg:text-base leading-relaxed">
              {t('categories.subtitle')}
            </p>
            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm text-red-600 font-medium">
                    {t('errors.loadingCategories')}: {error.message}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {categories.length > 0 && (
              <button
                onClick={() => setShowBulkTranslation(true)}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {t('actions.bulkTranslate')}
              </button>
            )}
            <button
              onClick={() => navigate({ to: '/categories/create' })}
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 hover:border-blue-700 transition-all duration-200 font-medium text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('categories.createCategory')}
            </button>
          </div>
        </div>
      </div>




      {categories.length === 0 ? (
        <div className="text-center py-12">
          <div className="max-w-md mx-auto">
            <div className="text-6xl mb-4">📂</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('categories.noCategoriesFound')}
            </h3>
            <p className="text-gray-500 mb-6">
              {t('categories.createFirstCategory')}
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate({ to: '/categories/create' })}
                variant="primary"
                className="w-full"
              >
                {t('categories.createCategory')}
              </Button>
              <Button
                onClick={async () => {
                  try {
                    // Import the seeding function
                    const { seedSampleData } = await import('../utils/seedDatabase')
                    console.log('🌱 Starting to seed categories...')
                    
                    const result = await seedSampleData()
                    queryClient.invalidateQueries({ queryKey: ['categories'] })
                    toast.success(`Successfully seeded ${result.categoriesCount} categories!`)
                  } catch (error) {
                    console.error('Seeding error:', error)
                    if (error instanceof Error) {
                      if (error.message.includes('permission')) {
                        toast.error('Permission denied! Make sure you have admin role in your user document.')
                      } else {
                        toast.error(`Seeding failed: ${error.message}`)
                      }
                    } else {
                      toast.error('Failed to seed categories')
                    }
                  }
                }}
                variant="secondary"
                className="w-full"
              >
                🌱 {t('actions.seedCategories')}
              </Button>
              <Button
                onClick={async () => {
                  try {
                    console.log('🗑️ Clearing categories...')
                    await adminService.clearAllCategories()
                    queryClient.invalidateQueries({ queryKey: ['categories'] })
                    toast.success('Categories cleared successfully!')
                  } catch (error) {
                    console.error('Clear error:', error)
                    toast.error('Failed to clear categories')
                  }
                }}
                variant="secondary"
                className="w-full"
              >
                🗑️ {t('buttons.clearAllCategories')}
              </Button>
              <Button
                onClick={async () => {
                  console.log('🔍 Debug: Checking database contents...')
                  
                  // Check categories collection
                  const categoriesResult = await adminService.getCategories('en')
                  console.log('📁 Categories (EN):', categoriesResult)
                  
                  const categoriesResultFr = await adminService.getCategories('fr') 
                  console.log('📁 Categories (FR):', categoriesResultFr)
                  
                  // Check raw collections
                  console.log('Run in console to see raw data:')
                  console.log('adminService.getCategories("en")')
                  console.log('adminService.getCategoryTranslations("9w3PJoX5y0UNpRcSRnxu")')
                }}
                variant="tertiary"
                className="w-full"
              >
                {t('actions.debugConsole')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <CategoryList
          categories={categories}
          onEdit={handleEdit}
          onTranslations={handleTranslations}
          onToggleStatus={handleToggleStatus}
          togglingCategoryId={togglingCategoryId}
        />
      )}

      {showTranslations && (
        <CategoryTranslationModal
          categoryId={showTranslations}
          onClose={() => setShowTranslations(null)}
        />
      )}

      {showBulkTranslation && (
        <BulkTranslationModal
          categories={categories.map(c => ({ id: c.id, name: c.name || c.id }))}
          onTranslate={handleBulkTranslation}
          onClose={() => setShowBulkTranslation(false)}
        />
      )}

      <TranslationProgressModal
        isOpen={showBulkProgressModal}
        progress={bulkTranslationProgress}
        onClose={() => {
          setShowBulkProgressModal(false)
          setBulkTranslationProgress({})
        }}
        title={t('actions.bulkTranslationProgress')}
        categories={categories.map(c => ({ id: c.id, name: c.name || c.id }))}
      />


    </div>
  )
}